import {
  useCallback,
  useMemo,
  useState,
  type MouseEvent,
  type PointerEvent
} from "react";
import { Show } from "shared/components";
import { cx } from "shared/utils";
import { JOURNEY_GEOMETRY as G, JOURNEY_TONES } from "./constants";
import {
  collectHighlight,
  type JourneyGraph,
  type JourneyNode,
  type JourneySelection
} from "./graph";
import {
  formatJourneyRate,
  layoutJourney,
  type LaidOutLink,
  type LaidOutNode
} from "./layout";
import { JourneyTooltip, type JourneyTooltipContent } from "./JourneyTooltip";

const formatCount = (count: number) => count.toLocaleString("en-US");

const toneOf = (node: JourneyNode | undefined) => {
  if (!node) return JOURNEY_TONES.progressed;
  // A step the cohort never reached is still shown — "no showings were
  // scheduled" is an answer — but nothing flowed through it, so it must not be
  // coloured as though something had.
  if (node.count === 0) return JOURNEY_TONES.empty;
  return node.kind === "outcome"
    ? JOURNEY_TONES.stalled
    : JOURNEY_TONES.progressed;
};

const plural = (count: number) =>
  `${formatCount(count)} ${count === 1 ? "lead" : "leads"}`;

const shareOfAll = (count: number, total: number) =>
  total > 0 ? `${formatJourneyRate(count / total)} of all leads created` : null;

const nodeTooltip = (
  node: JourneyNode,
  total: number
): JourneyTooltipContent => ({
  title: node.label,
  // The count is the headline, the percentages are the detail under it — the
  // same split the bar-chart tooltips use for their total.
  subtitle: plural(node.count),
  rows: [
    node.rateOfPrevious === null
      ? null
      : `${formatJourneyRate(node.rateOfPrevious)} of previous step`,
    shareOfAll(node.count, total)
  ].filter((row): row is string => row !== null)
});

const linkTooltip = (
  laid: LaidOutLink,
  graph: JourneyGraph
): JourneyTooltipContent => {
  const from = graph.nodeById.get(laid.fromId);
  const to = graph.nodeById.get(laid.toId);
  const title =
    laid.link.kind === "source"
      ? `${from?.label ?? ""} leads`
      : `${from?.label ?? ""} → ${to?.label ?? ""}`;
  const base = laid.link.kind === "source" ? graph.total : (from?.count ?? 0);
  return {
    title,
    subtitle: plural(laid.link.count),
    rows: [
      base > 0
        ? `${formatJourneyRate(laid.link.count / base)} of previous step`
        : null,
      shareOfAll(laid.link.count, graph.total)
    ].filter((row): row is string => row !== null)
  };
};

/**
 * The chart itself: ribbons, bars and the pills that label them.
 *
 * Drawn as one SVG with the pills as absolutely positioned buttons on top, so
 * every clickable part of the chart is a real focusable element and the whole
 * thing can be walked with the keyboard.
 */
export const LeadJourneySankey = ({
  graph,
  width,
  onSelect
}: {
  graph: JourneyGraph;
  width: number;
  onSelect: (selection: JourneySelection) => void;
}) => {
  const [highlighted, setHighlighted] = useState<Set<string> | null>(null);
  const [tooltip, setTooltip] = useState<
    (JourneyTooltipContent & { x: number; y: number }) | null
  >(null);

  const layout = useMemo(() => layoutJourney(graph, width), [graph, width]);

  // Both of these are read once per ribbon while drawing and again on every
  // hover, so they are indexed rather than scanned.
  const xById = useMemo(
    () => new Map(layout.nodes.map((node) => [node.id, node.x])),
    [layout.nodes]
  );
  const laidLinkById = useMemo(
    () => new Map(layout.links.map((laid) => [laid.id, laid])),
    [layout.links]
  );

  const tooltipFor = useCallback(
    (id: string): JourneyTooltipContent | null => {
      const node = graph.nodeById.get(id);
      if (node) return nodeTooltip(node, graph.total);
      const laid = laidLinkById.get(id);
      return laid ? linkTooltip(laid, graph) : null;
    },
    [graph, laidLinkById]
  );

  const showFor = useCallback(
    (id: string, x: number, y: number) => {
      const content = tooltipFor(id);
      if (!content) return;
      setHighlighted(collectHighlight(graph, id));
      setTooltip({ ...content, x, y });
    },
    [graph, tooltipFor]
  );

  const clear = useCallback(() => {
    setHighlighted(null);
    setTooltip(null);
  }, []);

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const target = (event.target as Element).closest<HTMLElement>(
        "[data-journey-id]"
      );
      if (!target) {
        clear();
        return;
      }
      showFor(target.dataset.journeyId!, event.clientX, event.clientY);
    },
    [clear, showFor]
  );

  // One click handler for the whole chart rather than one per element: bars,
  // pills, drop-off bands and ribbons all carry a `data-journey-id`, so
  // whatever is under the pointer opens a drill-down. Previously only the pill
  // was wired up, while the bars carried the pointer cursor — so clicking the
  // thing the cursor invited you to click did nothing.
  //
  // A ribbon's own id goes to the API rather than being collapsed onto one of
  // the bars it touches: the endpoint resolves a ribbon by the same bitmask
  // rules that drew it, so "Contacted → Application started" opens the 5 leads
  // that took that route, not the 24 sitting in the bar it lands on.
  const selectionFor = useCallback(
    (id: string): JourneySelection | undefined => {
      const node = graph.nodeById.get(id);
      if (node) return { id: node.id, label: node.label, count: node.count };

      const link = graph.linkById.get(id);
      if (!link) return undefined;

      // Same wording as the ribbon's tooltip, so the modal that opens is
      // headed by the thing that was just hovered.
      const from = graph.nodeById.get(link.from_id);
      const to = graph.nodeById.get(link.to_id);
      return {
        id,
        label:
          link.kind === "source"
            ? `${from?.label ?? ""} leads`
            : `${from?.label ?? ""} \u2192 ${to?.label ?? ""}`,
        count: link.count
      };
    },
    [graph]
  );

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const target = (event.target as Element).closest<HTMLElement>(
        "[data-journey-id]"
      );
      if (!target) return;

      const selection = selectionFor(target.dataset.journeyId!);
      if (selection) onSelect(selection);
    },
    [selectionFor, onSelect]
  );

  const dimmed = useCallback(
    (id: string) => highlighted !== null && !highlighted.has(id),
    [highlighted]
  );

  if (layout.nodes.length === 0) return null;

  return (
    <div
      className="relative"
      style={{ width: layout.width, height: layout.height }}
      onPointerMove={handlePointerMove}
      onPointerLeave={clear}
      onClick={handleClick}
    >
      <svg
        width={layout.width}
        height={layout.height}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        aria-hidden
        className="block"
      >
        <defs>
          {layout.links.map((laid) => (
            <linearGradient
              key={laid.id}
              id={gradientId(laid.id)}
              gradientUnits="userSpaceOnUse"
              x1={(xById.get(laid.fromId) ?? 0) + G.nodeWidth}
              y1={0}
              x2={xById.get(laid.toId) ?? 0}
              y2={0}
            >
              <stop
                offset="0"
                stopColor={toneOf(graph.nodeById.get(laid.fromId))}
              />
              <stop
                offset="1"
                stopColor={toneOf(graph.nodeById.get(laid.toId))}
              />
            </linearGradient>
          ))}
        </defs>
        {layout.links.map((laid) => (
          <path
            key={laid.id}
            d={laid.path}
            data-journey-id={laid.id}
            fill={`url(#${gradientId(laid.id)})`}
            className={cx(
              "transition-opacity duration-150 cursor-pointer",
              ribbonOpacity(highlighted, laid.id)
            )}
          />
        ))}
        {layout.nodes.map((node) => (
          <rect
            key={node.id}
            x={node.x}
            y={node.y}
            width={G.nodeWidth}
            height={node.height}
            rx={G.nodeRadius}
            fill={toneOf(node)}
            data-journey-id={node.id}
            className={cx(
              "transition-opacity duration-150 cursor-pointer",
              dimmed(node.id) ? "opacity-20" : "opacity-100"
            )}
          />
        ))}
      </svg>

      {/* A milestone nobody reached gets no pill. "Showing scheduled | 0 | 0%"
          is a label with nothing under it and no flow through it, and on a
          cohort that bypassed two steps the row turns into a run of zeroes
          that crowds out the numbers that do mean something. The step keeps
          its column and its flat tick, so the order still reads left to
          right. */}
      {layout.nodes
        .filter((node) => node.kind !== "milestone" || node.count > 0)
        .map((node) => (
          <JourneyPill
            key={node.id}
            node={node}
            dimmed={dimmed(node.id)}
            onShow={showFor}
            onHide={clear}
          />
        ))}

      <JourneyTooltip content={tooltip} />
    </div>
  );
};

// Ribbons are translucent at rest so overlapping paths stay readable; on
// hover the highlighted path comes forward and the rest recede almost to
// nothing.
const ribbonOpacity = (highlighted: Set<string> | null, id: string): string => {
  if (highlighted === null) return "opacity-[0.34]";
  return highlighted.has(id) ? "opacity-[0.88]" : "opacity-[0.07]";
};

const gradientId = (id: string) => `lj-${id.replace(/[^a-zA-Z0-9]/g, "-")}`;

/**
 * The label on a node: name, count and its share of the previous step. It is a
 * button because clicking it opens the leads behind the number — the same
 * action as clicking the bar, so the two never disagree.
 */
const JourneyPill = ({
  node,
  dimmed,
  onShow,
  onHide
}: {
  node: LaidOutNode;
  dimmed: boolean;
  onShow: (id: string, x: number, y: number) => void;
  onHide: () => void;
}) => {
  const rate = formatJourneyRate(node.rateOfPrevious);
  const centre = node.y + node.height / 2;

  const style = (() => {
    if (node.pillAnchor === "corner")
      return {
        left: node.x + G.nodeWidth,
        top: node.y + G.labelDrop,
        transform: "translate(0, -100%)",
        borderRadius: "999px 999px 999px 0"
      };
    if (node.pillAnchor === "before")
      return {
        left: node.x,
        top: centre + G.labelDrop,
        transform: "translate(-100%, -100%)",
        borderRadius: "999px 999px 0 999px"
      };
    return {
      left: node.x + G.nodeWidth,
      top: centre + G.labelDrop,
      transform: "translate(0, -100%)",
      borderRadius: "999px 999px 999px 0"
    };
  })();

  return (
    <button
      type="button"
      data-journey-id={node.id}
      style={style}
      onFocus={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        onShow(node.id, rect.left + rect.width / 2, rect.top);
      }}
      onBlur={onHide}
      aria-label={`${node.label}: ${plural(node.count)}${
        rate ? `, ${rate} of the previous step` : ""
      }. Show these leads`}
      className={cx(
        "absolute z-[3] flex items-center gap-1.5 whitespace-nowrap bg-content-1000-inverted px-2.5 py-1",
        "shadow-xs ring-1 ring-alpha-100 transition-opacity duration-150",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500",
        dimmed ? "opacity-30" : "opacity-100"
      )}
    >
      <span
        className="size-2 shrink-0 rounded-full"
        style={{ backgroundColor: toneOf(node) }}
      />
      <span className="text-xs font-medium text-content-700">{node.label}</span>
      <span className="h-3 w-px shrink-0 bg-alpha-100" />
      <span className="text-xs font-medium tabular-nums text-content-1000">
        {formatCount(node.count)}
      </span>
      <Show if={!!rate}>
        <span className="text-xs font-medium tabular-nums text-content-300">
          {rate}
        </span>
      </Show>
    </button>
  );
};
